import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, ChevronDown, ChevronRight, Lock, Clock, Terminal, ArrowRight, KeyRound, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Rule = () => <div className="h-px bg-slate-200" />;

const LabRow = ({ lab, locked }) => (
  <div
    className="group py-6 border-b border-slate-100 dark:border-white/5 last:border-0"
    data-testid={`cc101-lab-${lab.slug}`}
  >
    <div className="flex items-baseline gap-4">
      <span className="font-mono text-xs text-slate-400 dark:text-zinc-600 w-6 shrink-0 tabular-nums">
        {String(lab.n).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 flex-wrap">
          <h4 className="text-base font-medium text-slate-900 dark:text-zinc-100">{lab.title}</h4>
          {lab.completed && (
            <span
              className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-400/10 border border-emerald-200 dark:border-emerald-400/30 rounded-full px-2 py-0.5"
              data-testid={`cc101-done-${lab.slug}`}
            >
              <Check className="w-3 h-3" /> Completed
            </span>
          )}
          {!lab.available && (
            <span className="text-xs text-slate-500 dark:text-zinc-500 bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 rounded-full px-2 py-0.5">
              Coming in module {lab.n <= 7 ? 2 : 3}
            </span>
          )}
        </div>

        <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">{lab.teaches}</p>

        <p className="mt-2.5 text-xs text-slate-500 dark:text-zinc-500 leading-relaxed max-w-2xl">
          <span className="font-medium text-slate-600 dark:text-zinc-400">The verifier: </span>
          {lab.verifier}
        </p>

        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500 dark:text-zinc-500">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> {lab.minutes} min
          </span>
          <span className="font-mono">{lab.cost} API</span>
          {lab.launchable ? (
            <Link
              to={`/labs/${lab.slug}`}
              className="inline-flex items-center gap-1 text-slate-900 dark:text-zinc-100 font-medium hover:gap-2 transition-all"
              data-testid={`cc101-open-${lab.slug}`}
            >
              Open lab <ArrowRight className="w-3 h-3" />
            </Link>
          ) : lab.available && locked ? (
            <span className="inline-flex items-center gap-1 text-slate-400 dark:text-zinc-600">
              <Lock className="w-3 h-3" /> Subscribers
            </span>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

const Module = ({ module, locked, open, onToggle }) => (
  <section className="border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden bg-white dark:bg-[#0A0A0A]">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-start gap-4 px-6 py-5 text-left hover:bg-slate-50 dark:bg-white/[0.03] transition-colors"
      data-testid={`cc101-module-${module.key}`}
    >
      {open ? (
        <ChevronDown className="w-4 h-4 text-slate-400 dark:text-zinc-600 mt-1 shrink-0" />
      ) : (
        <ChevronRight className="w-4 h-4 text-slate-400 dark:text-zinc-600 mt-1 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-slate-400 dark:text-zinc-600">MODULE {module.number}</span>
          <h3 className="text-lg font-medium text-slate-900 dark:text-zinc-100">{module.title}</h3>
        </div>
        <p className="mt-1.5 text-sm text-slate-600 dark:text-zinc-400 max-w-2xl leading-relaxed">{module.summary}</p>
      </div>
      <div className="text-right shrink-0">
        <span className="font-mono text-sm text-slate-900 dark:text-zinc-100 tabular-nums">
          {module.completed}/{module.total}
        </span>
        <span className="block text-xs text-slate-400 dark:text-zinc-600 mt-0.5">done</span>
      </div>
    </button>
    {open && (
      <div className="px-6 pb-2 border-t border-slate-100 dark:border-white/5">
        {module.labs.map((lab) => (
          <LabRow key={lab.slug} lab={lab} locked={locked} />
        ))}
      </div>
    )}
  </section>
);

const KeyCheck = () => {
  const [key, setKey] = useState("");
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    if (!key.trim()) return;
    setBusy(true);
    setState(null);
    try {
      const { data } = await axios.post(
        `${API}/labs/cc101-meet-agent/preflight-key`,
        { api_key: key.trim() },
        { withCredentials: true }
      );
      setState(data);
    } catch {
      setState({ valid: false, reason: "Could not run the check. Try again in a moment." });
    } finally {
      setBusy(false);
      setKey("");
    }
  };

  return (
    <div className="mt-6 p-5 border border-slate-200 dark:border-white/10 rounded-lg bg-slate-50/60 dark:bg-white/[0.03]">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-zinc-100">
        <KeyRound className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> Check your key now
      </div>
      <p className="mt-1.5 text-xs text-slate-600 dark:text-zinc-400">
        Paste it to confirm it works before you start a lab. It is sent once to Anthropic
        for the check and never stored.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && check()}
          placeholder="sk-ant-..."
          className="flex-1 min-w-0 px-3 py-2 text-sm font-mono border border-slate-300 dark:border-white/15 rounded-md bg-white dark:bg-[#0A0A0A] focus:outline-none focus:border-slate-900"
          data-testid="cc101-key-input"
        />
        <button
          type="button"
          onClick={check}
          disabled={busy || !key.trim()}
          className="px-4 py-2 text-sm font-medium bg-slate-900 text-white rounded-md hover:bg-slate-800 disabled:opacity-40 transition-colors"
          data-testid="cc101-key-check"
        >
          {busy ? "Checking…" : "Check"}
        </button>
      </div>
      {state && (
        <p
          className={`mt-3 text-xs leading-relaxed ${state.valid ? "text-emerald-700 dark:text-emerald-300" : "text-red-700 dark:text-red-400"}`}
          data-testid="cc101-key-result"
        >
          {state.valid ? "✓ " : "✗ "}
          {state.reason}
        </p>
      )}
    </div>
  );
};

export default function CourseClaudeCode101() {
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/courses/claude-code-101`, { withCredentials: true })
      .then(({ data }) => {
        setCourse(data);
        // Only the module you are working in is expanded: ten labs should read
        // as a path, not a backlog.
        const current =
          data.modules.find((m) => m.completed < m.available && m.available > 0) ||
          data.modules[0];
        setOpenKey(current?.key || null);
      })
      .catch(() => setCourse(false));
  }, []);

  if (course === null) {
    return <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="cc101-loading" />;
  }
  if (course === false) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] px-6 py-24 text-center text-slate-600 dark:text-zinc-400">
        Could not load the course. Please refresh.
      </div>
    );
  }

  const locked = !course.has_access;
  const pct = course.available_labs
    ? Math.round((course.completed_labs / course.total_labs) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="cc101-course-page">
      <div className="max-w-4xl mx-auto px-6 py-16 sm:py-24">
        <p className="font-mono text-xs text-sky-700 dark:text-sky-400 tracking-wide">COURSE</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold text-slate-900 dark:text-zinc-100 tracking-tight">
          Claude Code 101
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
          Ten hands-on labs on driving an AI coding agent properly — in a live workstation
          running the real Claude Code CLI, graded by verifiers that run your work rather
          than asking a model whether you did well.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-600 dark:text-zinc-400">
          <span className="inline-flex items-center gap-2">
            <Terminal className="w-4 h-4 text-slate-400 dark:text-zinc-600" /> {course.total_labs} labs, 3 modules
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400 dark:text-zinc-600" /> 30–45 min each
          </span>
          <span className="font-mono text-xs">{course.available_labs} available now</span>
        </div>

        {!locked && course.available_labs > 0 && (
          <div className="mt-8" data-testid="cc101-progress">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-slate-600 dark:text-zinc-400">Your progress</span>
              <span className="font-mono text-slate-900 dark:text-zinc-100 tabular-nums">
                {course.completed_labs} of {course.total_labs}
              </span>
            </div>
            <div className="mt-2 h-1 bg-slate-100 dark:bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-slate-900 transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        {locked && (
          <div
            className="mt-10 p-6 border border-slate-900 rounded-xl bg-slate-900 text-white"
            data-testid="cc101-paywall"
          >
            <h2 className="text-base md:text-lg font-medium">Included in the ILFAL subscription</h2>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-xl">
              All ten labs need an active subscription — the same one that covers every
              other track. No separate purchase, and nothing extra to pay for this course.
              The full curriculum is below so you can see exactly what you would be getting.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link
                to={user ? "/subscription" : "/register"}
                className="px-5 py-2.5 bg-white dark:bg-[#0A0A0A] text-slate-900 dark:text-zinc-100 text-sm font-medium rounded-md hover:bg-slate-100 dark:bg-white/10 transition-colors"
                data-testid="cc101-subscribe"
              >
                {user ? "View subscription" : "Get access"}
              </Link>
              {!user && (
                <Link
                  to="/login"
                  className="px-5 py-2.5 border border-white/25 text-white text-sm font-medium rounded-md hover:bg-white/10 transition-colors"
                  data-testid="cc101-signin"
                >
                  Sign in
                </Link>
              )}
            </div>
          </div>
        )}

        {course.locked_completions?.length > 0 && (
          <div
            className="mt-6 p-5 border border-amber-200 dark:border-amber-400/30 rounded-lg bg-amber-50 dark:bg-amber-400/10"
            data-testid="cc101-kept-progress"
          >
            <h3 className="text-sm font-medium text-amber-900 dark:text-amber-200">
              Your earlier progress is still here
            </h3>
            <p className="mt-1.5 text-sm text-amber-800 dark:text-amber-300 leading-relaxed">
              You completed{" "}
              {course.locked_completions.map((l) => l.title).join(", ")} while this course
              was free. Those completions are kept, not deleted — the course is now part of
              the subscription, and your progress is waiting for you.
            </p>
          </div>
        )}

        <div className="mt-14 space-y-4">
          {course.modules.map((m) => (
            <Module
              key={m.key}
              module={m}
              locked={locked}
              open={openKey === m.key}
              onToggle={() => setOpenKey(openKey === m.key ? null : m.key)}
            />
          ))}
        </div>

        <div className="mt-16">
          <Rule />
          <div className="pt-10">
            <h2 className="text-lg font-medium text-slate-900 dark:text-zinc-100">{course.byo_key.heading}</h2>
            {course.byo_key.body.map((para, i) => (
              <p key={i} className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
                {para}
              </p>
            ))}
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm">
              <a
                href={course.byo_key.get_key_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-slate-900 dark:text-zinc-100 font-medium hover:gap-2 transition-all"
                data-testid="cc101-get-key"
              >
                Create an API key <ExternalLink className="w-3 h-3" />
              </a>
              <Link
                to={course.setup_doc_path}
                className="inline-flex items-center gap-1.5 text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:text-zinc-100 transition-colors"
                data-testid="cc101-setup-docs"
              >
                Installing Claude Code (free guide) <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {user && <KeyCheck />}
          </div>
        </div>

        <div className="mt-16">
          <Rule />
          <div className="pt-10">
            <h2 className="text-lg font-medium text-slate-900 dark:text-zinc-100">Certificate</h2>
            <p className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
              {course.certificate.note} It carries your name, the labs you completed, an
              issue date and a unique ID, with a public page anyone can visit to confirm it
              is real.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
